/**
 * Dosya seçici: solda kaynaklar (telefonda çipler), ortada ızgara ya da yükleme
 * paneli, altta seçim çubuğu. Modalın her yerine dosya bırakılabilir.
 */

import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { UploadCloud, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { libraryGlowButtonClass } from "@/features/library/lib/library-ui";
import { cn } from "@/lib/utils";
import { usePickerDropPaste } from "../hooks/use-picker-drop-paste";
import { useFilePickerModal, useFilePickerNavigation, useFilePickerStore } from "../store/file-picker-store";
import { PickerGrid } from "./PickerGrid";
import { PickerQuickLook } from "./PickerQuickLook";
import { PickerSelectionTray } from "./PickerSelectionTray";
import { PickerSourcesNav } from "./PickerSourcesNav";
import { PickerUploadPanel } from "./PickerUploadPanel";

const DropOverlay = () => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center p-4 bg-bg-primary/70 dark:bg-gray-950/70 backdrop-blur-sm"
    >
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-violet-400/70 dark:border-violet-400/60 bg-gradient-to-br from-indigo-500/10 via-violet-500/10 to-fuchsia-500/10">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 shadow-lg shadow-violet-500/30">
          <UploadCloud className="w-7 h-7 text-white" />
        </div>
        <p className="text-base font-semibold text-text-primary dark:text-white">
          {t("filePicker.dropAnywhere")}
        </p>
      </div>
    </motion.div>
  );
};

export const FilePickerModal = () => {
  const { t } = useTranslation();
  const { isOpen, config, closePicker } = useFilePickerModal();
  const { source } = useFilePickerNavigation();
  const quickLookId = useFilePickerStore((state) => state.quickLookId);
  const setQuickLookId = useFilePickerStore((state) => state.setQuickLookId);
  const { isDragging, dropHandlers } = usePickerDropPaste();

  const handleOpenChange = (open: boolean) => {
    // Onayla kapanışta store zaten kapalı; burada yalnızca kullanıcı kapatmaları kalır
    if (!open && useFilePickerStore.getState().isOpen) closePicker("cancel");
  };

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    if (!quickLookId) return;
    event.preventDefault();
    setQuickLookId(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        open={isOpen}
        cleanupMode="scoped"
        onEscapeKeyDown={handleEscapeKeyDown}
        {...dropHandlers}
        className={cn(
          "p-0 gap-0 flex flex-col overflow-hidden",
          "max-sm:fixed max-sm:inset-0 max-sm:h-[100dvh] max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:rounded-none max-sm:border-0",
          "sm:w-[92vw] sm:max-w-[1200px] sm:h-[86dvh] sm:max-h-[90dvh] md:max-h-[90dvh]",
          "bg-bg-primary/95 dark:bg-gray-950/90 backdrop-blur-2xl",
          "sm:border sm:border-black/[0.06] dark:sm:border-white/[0.08] shadow-2xl"
        )}
      >
        <header className="shrink-0 flex items-start gap-3 px-4 sm:px-5 pt-[max(0.875rem,env(safe-area-inset-top))] sm:pt-4 pb-3 border-b border-black/[0.06] dark:border-white/[0.08]">
          <div className="min-w-0 flex-1">
            <DialogTitle className="truncate text-base sm:text-lg font-semibold text-text-primary dark:text-white">
              {config?.title || t("filePicker.title")}
            </DialogTitle>
            <DialogDescription className="truncate text-xs sm:text-sm text-text-tertiary dark:text-gray-400">
              {config?.description || t("filePicker.description")}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => closePicker("cancel")}
            aria-label={t("common.close")}
            className={cn(
              libraryGlowButtonClass,
              "w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-text-tertiary dark:text-gray-400 hover:text-text-primary dark:hover:text-white"
            )}
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </header>

        <div className="lg:hidden shrink-0 border-b border-black/[0.06] dark:border-white/[0.08]">
          <PickerSourcesNav layout="chips" />
        </div>

        <div className="relative flex min-h-0 flex-1">
          <aside className="hidden lg:flex w-60 shrink-0 flex-col overflow-y-auto border-r border-black/[0.06] dark:border-white/[0.08]">
            <PickerSourcesNav layout="sidebar" />
          </aside>
          <main className="relative flex min-h-0 min-w-0 flex-1 flex-col">
            {source === "upload" ? <PickerUploadPanel /> : <PickerGrid />}
          </main>
        </div>

        <PickerSelectionTray />

        <AnimatePresence>{quickLookId && <PickerQuickLook key="quick-look" />}</AnimatePresence>
        <AnimatePresence>{isDragging && <DropOverlay key="drop-overlay" />}</AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
